import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Upload, X, Video } from "lucide-react";

interface VideoUploadProps {
  onVideoUploaded: (url: string) => void;
  currentVideoUrl?: string;
  bucketName?: string;
  folder?: string;
  className?: string;
  maxSizeMB?: number;
}

export const VideoUpload = ({
  onVideoUploaded,
  currentVideoUrl,
  bucketName = "facility-videos",
  folder = "",
  className = "",
  maxSizeMB = 50,
}: VideoUploadProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentVideoUrl || null);
  const { toast } = useToast();

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    console.log("Video file selected:", file.name, file.type, file.size);

    // Validate file type
    const validTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'];
    if (!validTypes.includes(file.type)) {
      console.error("Invalid file type:", file.type);
      toast({
        title: "Invalid file type",
        description: "Please select a video file (MP4, WebM, MOV, AVI).",
        variant: "destructive",
      });
      return;
    }

    // Validate file size
    const maxSize = maxSizeMB * 1024 * 1024;
    if (file.size > maxSize) {
      console.error("File too large:", file.size);
      toast({
        title: "File too large",
        description: `Please select a video smaller than ${maxSizeMB}MB.`,
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    console.log("Starting video upload...");

    try {
      // Create a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
      const filePath = folder ? `${folder}/${fileName}` : fileName;

      console.log("Uploading to bucket:", bucketName, "path:", filePath);

      // Upload file to Supabase Storage
      const { data, error } = await supabase.storage
        .from(bucketName)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error("Upload error:", error);
        throw error;
      }

      console.log("Upload successful:", data);

      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from(bucketName)
        .getPublicUrl(filePath);

      console.log("Public URL:", publicUrl);

      setPreviewUrl(publicUrl);
      onVideoUploaded(publicUrl);
      
      toast({
        title: "Video uploaded",
        description: "Your video has been uploaded successfully.",
      });

    } catch (error: any) {
      console.error("Error uploading video:", error);
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload video. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const removeVideo = () => {
    console.log("Removing video");
    setPreviewUrl(null);
    onVideoUploaded("");
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {previewUrl ? (
        <div className="relative">
          <video
            src={previewUrl}
            className="w-full h-48 object-cover rounded-lg border bg-black"
            onError={(e) => {
              console.error("Preview video failed to load");
              setPreviewUrl(null);
            }}
          />
          <Button
            type="button"
            variant="destructive"
            size="sm"
            className="absolute top-2 right-2"
            onClick={removeVideo}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <div className="border-2 border-dashed border-muted-foreground/30 rounded-lg p-8 text-center">
          <Video className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-sm text-muted-foreground mb-4">
            Click to upload a video
          </p>
          <p className="text-xs text-muted-foreground/70 mb-4">
            MP4, WebM, MOV, AVI up to {maxSizeMB}MB
          </p>
        </div>
      )}

      <div className="flex items-center space-x-2">
        <Input
          type="file"
          accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
          onChange={handleFileChange}
          disabled={isUploading}
          className="hidden"
          id="video-upload"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => document.getElementById('video-upload')?.click()}
          disabled={isUploading}
          className="w-full"
        >
          <Upload className="h-4 w-4 mr-2" />
          {isUploading ? "Uploading..." : "Choose Video"}
        </Button>
      </div>
    </div>
  );
};
